import { useCallback, useEffect, useRef, useState } from "react";
import {
	AGENT_COMPANION_COOLDOWN_MS,
	AGENT_COMPANION_CUES,
	AGENT_COMPANION_DEFAULT_VARIANT,
	AGENT_COMPANION_EXPRESSIONS,
	AGENT_COMPANION_FOCUS_DELAY_MS,
	AGENT_COMPANION_FOCUS_THROTTLE_MS,
	AGENT_COMPANION_GLIMPSE_MS,
	AGENT_COMPANION_HOLD_MS,
	AGENT_COMPANION_IDLE_MS,
	AGENT_COMPANION_KEYWORD,
	AGENT_COMPANION_LAST_SEEN_KEY,
	AGENT_COMPANION_OFF_KEY,
	AGENT_COMPANION_RANDOM_CHANCE,
	AGENT_COMPANION_RANDOM_DELAY_MS,
	AGENT_COMPANION_VARIANTS,
	AgentCompanionExpression,
	AgentCompanionTrigger,
	AgentCompanionVariant,
} from "./agentCompanion.constants";

/**
 * Where on screen to draw the companion, when it is peeking at something.
 *
 * Viewport coordinates taken at the moment of the appearance. They are not
 * tracked afterwards: a peek is over in about two seconds, and following a
 * scrolling element around would mean a layout read per frame on a page people
 * work in all day.
 */
export interface AgentCompanionAnchor {
	top: number;
	left: number;
	width: number;
	height: number;
}

export interface AgentCompanionAppearance {
	trigger: AgentCompanionTrigger;
	expression: AgentCompanionExpression;
	line: string;
	/** Changes every appearance so the entrance animation restarts. */
	id: number;
	/** Null means the default corner. */
	anchor: AgentCompanionAnchor | null;
}

export interface UseAgentCompanionAppearanceResult {
	appearance: AgentCompanionAppearance | null;
	/** Call on a genuine win — a plan saved, a config generated. */
	celebrate: () => void;
	dismiss: () => void;
}

/**
 * Storage access throws outright in some privacy modes, so every read is
 * guarded and every failure means "no preference recorded" rather than an
 * exception on a page that has nothing to do with the mascot.
 */
const readStorage = (
	store: "local" | "session",
	key: string,
): string | null => {
	try {
		const target =
			store === "local" ? window.localStorage : window.sessionStorage;
		return target.getItem(key);
	} catch {
		return null;
	}
};

const writeSession = (key: string, value: string): void => {
	try {
		window.sessionStorage.setItem(key, value);
	} catch {
		// Nothing to do — a missed cooldown stamp only risks one extra wave.
	}
};

/**
 * The companion stays away entirely when:
 *
 * 1. the browser is being driven by automation. Playwright sets
 *    `navigator.webdriver`, and E2E drives this exact page through the UI with
 *    real keystrokes — a floating character would be one more thing for a
 *    locator to match and one more source of flake. This is the important one.
 * 2. someone opted out with `localStorage.setItem("agent-companion:off", "1")`.
 * 3. the URL says `?agent-companion=off`, for a one-off clean screenshot.
 */
export const isAgentCompanionSuppressed = (): boolean => {
	if (typeof navigator !== "undefined" && navigator.webdriver) return true;
	if (readStorage("local", AGENT_COMPANION_OFF_KEY) === "1") return true;
	return resolveAgentCompanionParam() === "off";
};

const resolveAgentCompanionParam = (): string | null => {
	try {
		return new URLSearchParams(window.location.search).get("agent-companion");
	} catch {
		return null;
	}
};

/**
 * Which candidate to draw. `?agent-companion=heart` / `?agent-companion=pathfinder` exists so the
 * three can be shown side by side without a rebuild while the team decides.
 */
export const resolveAgentCompanionVariant = (): AgentCompanionVariant => {
	const requested = resolveAgentCompanionParam();
	const match = AGENT_COMPANION_VARIANTS.find(
		(variant) => variant === requested,
	);
	return match ?? AGENT_COMPANION_DEFAULT_VARIANT;
};

/** `?agent-companion-face=proud` pins the expression, for the same reason. */
export const resolveAgentCompanionExpressionOverride =
	(): AgentCompanionExpression | null => {
		try {
			const requested = new URLSearchParams(window.location.search).get(
				"agent-companion-face",
			);
			return (
				AGENT_COMPANION_EXPRESSIONS.find(
					(expression) => expression === requested,
				) ?? null
			);
		} catch {
			return null;
		}
	};

const withinCooldown = (): boolean => {
	const last = Number(readStorage("session", AGENT_COMPANION_LAST_SEEN_KEY));
	// A missing or unparseable stamp reads as NaN, which compares false — no
	// cooldown, which is the harmless direction to fail in.
	return Date.now() - last < AGENT_COMPANION_COOLDOWN_MS;
};

/**
 * Decides when the companion turns up on a page, and for how long.
 *
 * Deliberately separate from the rendering: the triggers are the part with
 * behaviour worth testing, and they are unaffected by which character wins.
 */
export const useAgentCompanion = (): UseAgentCompanionAppearanceResult => {
	const [appearance, setAppearance] = useState<AgentCompanionAppearance | null>(
		null,
	);
	const nextId = useRef(0);

	const show = useCallback(
		(
			trigger: AgentCompanionTrigger,
			respectCooldown: boolean,
			anchor: AgentCompanionAnchor | null = null,
		): void => {
			if (isAgentCompanionSuppressed()) return;
			if (respectCooldown && withinCooldown()) return;

			const cue = AGENT_COMPANION_CUES[trigger];
			const line = cue.lines[Math.floor(Math.random() * cue.lines.length)];
			nextId.current += 1;
			// A peek is not an appearance for cooldown purposes — it has its own
			// throttle, and letting it stamp this would mute the occasions.
			if (trigger !== "focus") {
				writeSession(AGENT_COMPANION_LAST_SEEN_KEY, String(Date.now()));
			}
			setAppearance({
				trigger,
				expression: resolveAgentCompanionExpressionOverride() ?? cue.expression,
				line,
				id: nextId.current,
				anchor,
			});
		},
		[],
	);

	const dismiss = useCallback(() => setAppearance(null), []);

	// Leave on their own after a beat. Anything that outstays its welcome on a
	// page people work in all day stops being charming very quickly.
	useEffect(() => {
		if (!appearance) return undefined;
		const timer = window.setTimeout(
			() => setAppearance(null),
			appearance.trigger === "focus"
				? AGENT_COMPANION_GLIMPSE_MS
				: AGENT_COMPANION_HOLD_MS,
		);
		return () => window.clearTimeout(timer);
	}, [appearance]);

	/**
	 * Trigger 4 — a peek at the field being filled in.
	 *
	 * The frequent one, and the reason the others could stay rare: it needs no
	 * luck and no waiting, so the character is actually part of the page rather
	 * than a thing people are told exists. Rationed by a throttle rather than
	 * the cooldown, and delayed slightly so tabbing through a form does not
	 * strobe it from field to field.
	 */
	useEffect(() => {
		if (isAgentCompanionSuppressed()) return undefined;

		let timer = 0;
		let lastPeek = 0;

		const onFocusIn = (event: FocusEvent) => {
			const target = event.target as HTMLElement | null;
			if (!target) return;
			const tag = target.tagName;
			if (tag !== "INPUT" && tag !== "TEXTAREA" && !target.isContentEditable) {
				return;
			}
			// Never beside a password box. Nothing reads worse than a cartoon
			// character watching someone type a credential.
			if ((target as HTMLInputElement).type === "password") return;

			const now = Date.now();
			if (now - lastPeek < AGENT_COMPANION_FOCUS_THROTTLE_MS) return;
			lastPeek = now;

			window.clearTimeout(timer);
			timer = window.setTimeout(() => {
				// Read the box late, so a field that moved while the form settled
				// is not peeked at in its old position.
				const rect = target.getBoundingClientRect();
				if (!rect.width && !rect.height) return;
				show("focus", false, {
					top: rect.top,
					left: rect.left,
					width: rect.width,
					height: rect.height,
				});
			}, AGENT_COMPANION_FOCUS_DELAY_MS);
		};

		window.addEventListener("focusin", onFocusIn);
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener("focusin", onFocusIn);
		};
	}, [show]);

	// Trigger 1 — a quiet page. Re-arming is throttled because `pointermove`
	// fires continuously and would otherwise churn a timer per mouse pixel.
	useEffect(() => {
		if (isAgentCompanionSuppressed()) return undefined;

		let timer = 0;
		let lastArmed = 0;
		const arm = () => {
			const now = Date.now();
			if (now - lastArmed < 1_000) return;
			lastArmed = now;
			window.clearTimeout(timer);
			timer = window.setTimeout(
				() => show("idle", true),
				AGENT_COMPANION_IDLE_MS,
			);
		};

		const events = ["pointerdown", "pointermove", "keydown", "scroll", "wheel"];
		events.forEach((event) =>
			window.addEventListener(event, arm, { passive: true }),
		);
		arm();

		return () => {
			window.clearTimeout(timer);
			events.forEach((event) => window.removeEventListener(event, arm));
		};
	}, [show]);

	// Trigger 2 — typing the name. Ignores text fields so it can never fire
	// while someone is filling in a treatment plan.
	useEffect(() => {
		if (isAgentCompanionSuppressed()) return undefined;

		let buffer = "";
		const onKeyDown = (event: KeyboardEvent) => {
			const target = event.target as HTMLElement | null;
			if (
				target?.tagName === "INPUT" ||
				target?.tagName === "TEXTAREA" ||
				target?.isContentEditable
			) {
				return;
			}
			if (event.key.length !== 1) return;

			buffer = (buffer + event.key.toLowerCase()).slice(
				-AGENT_COMPANION_KEYWORD.length,
			);
			if (buffer === AGENT_COMPANION_KEYWORD) {
				buffer = "";
				// Summoned on purpose, so the cooldown does not apply.
				show("keyword", false);
			}
		};

		window.addEventListener("keydown", onKeyDown);
		return () => window.removeEventListener("keydown", onKeyDown);
	}, [show]);

	// Trigger 3 — an occasional hello on arrival.
	useEffect(() => {
		if (isAgentCompanionSuppressed()) return undefined;
		const timer = window.setTimeout(() => {
			if (Math.random() < AGENT_COMPANION_RANDOM_CHANCE) show("random", true);
		}, AGENT_COMPANION_RANDOM_DELAY_MS);
		return () => window.clearTimeout(timer);
	}, [show]);

	const celebrate = useCallback(() => show("celebrate", false), [show]);

	return { appearance, celebrate, dismiss };
};

export default useAgentCompanion;
